/**
 * Global Prestige States — derives elite badges from the EXISTING
 * AdaptiveFanProfile counters + Fan DNA. Pure read, no I/O.
 *
 * Elite tiers expand "Featured Fans" with a global flavor:
 *   Global Predictor · Nation Leader · Viral Fan ·
 *   Momentum Master · Tactical Oracle · World Cup Elite
 */
import type { AdaptiveFanProfile } from "@/lib/adaptiveFan/adaptiveFanProfile";

export type PrestigeState =
  | "Global Predictor"
  | "Nation Leader"
  | "Viral Fan"
  | "Momentum Master"
  | "Tactical Oracle"
  | "World Cup Elite";

export interface PrestigeBadge {
  state: PrestigeState;
  emoji: string;
  tagline: string;
  color: string;
  /** 0..1 — strength relative to thresholds. */
  weight: number;
}

const META: Record<PrestigeState, Omit<PrestigeBadge, "weight">> = {
  "Global Predictor": { state: "Global Predictor", emoji: "🌐", tagline: "Lee el Mundial antes que nadie", color: "oklch(0.7 0.18 250)" },
  "Nation Leader":    { state: "Nation Leader",    emoji: "🚩", tagline: "Líder emocional de tu nación", color: "oklch(0.72 0.22 30)"  },
  "Viral Fan":        { state: "Viral Fan",        emoji: "🔥", tagline: "Tu reacción mueve a la multitud", color: "oklch(0.7 0.27 330)" },
  "Momentum Master":  { state: "Momentum Master",  emoji: "⚡", tagline: "Detectas el cambio antes del giro", color: "oklch(0.78 0.2 60)" },
  "Tactical Oracle":  { state: "Tactical Oracle",  emoji: "♟️", tagline: "Lees la pizarra como nadie",       color: "oklch(0.55 0.1 250)" },
  "World Cup Elite":  { state: "World Cup Elite",  emoji: "👑", tagline: "Élite mundial verificada",         color: "oklch(0.78 0.18 145)" },
};

export function derivePrestige(profile: AdaptiveFanProfile): PrestigeBadge {
  const c = profile.counters;
  const total =
    c.statsOpens + c.insightsOpens + c.syncOpens + c.pollVotes +
    c.predictionAnswers + c.reactions + c.socialOpens + c.activeMinutes;

  // Elite ceiling — verified across all axes.
  if (
    total >= 200 &&
    profile.confidence >= 0.7 &&
    c.predictionAnswers >= 30 &&
    c.reactions >= 30 &&
    c.statsOpens >= 15
  ) {
    return { ...META["World Cup Elite"], weight: 1 };
  }

  // Pick the dominant axis.
  const axes: Array<{ state: PrestigeState; score: number }> = [
    { state: "Tactical Oracle",  score: c.statsOpens * 1.4 + c.insightsOpens * 1.1 },
    { state: "Global Predictor", score: c.predictionAnswers * 1.5 + c.syncOpens * 0.6 },
    { state: "Viral Fan",        score: c.reactions * 1.4 + c.socialOpens * 0.9 },
    { state: "Nation Leader",    score: c.socialOpens * 1.2 + c.pollVotes * 1.1 },
    { state: "Momentum Master",  score: c.pollVotes * 1.3 + c.activeMinutes * 0.05 },
  ];
  axes.sort((a, b) => b.score - a.score);
  const top = axes[0];
  const weight = Math.max(0.1, Math.min(1, top.score / 60));
  return { ...META[top.state], weight };
}

export const PRESTIGE_META = META;